import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { initMercadoPago } from '@mercadopago/sdk-react';
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { ProductCatalog } from "./components/ProductCatalog";
import { AboutUs } from "./components/AboutUs";
import { RefillSection } from "./components/RefillSection";
import { Wholesale } from "./components/Wholesale";
import { FAQ } from "./components/FAQ";
import { Contact } from "./components/Contact";
import { Footer } from "./components/Footer";
import { Cart } from "./components/Cart";
import { Checkout } from "./components/Checkout";
import { PaymentResult } from "./components/PaymentResult";
import { WhatsAppButton } from "./components/WhatsAppButton";
import AdminLogin from "./components/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminOrders from "./components/admin/AdminOrders";
import AdminProducts from "./components/admin/AdminProducts";
import AdminProtected from "./components/admin/AdminProtected";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Initialize MercadoPago
initMercadoPago(process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY, {
  locale: 'es-MX'
});

const Home = ({ onAddToCart, cartItemsCount, onCartOpen }) => {
  return (
    <>
      <Header 
        cartItemsCount={cartItemsCount} 
        onCartOpen={onCartOpen} 
      />
      <Hero />
      <ProductCatalog onAddToCart={onAddToCart} />
      <RefillSection />
      <AboutUs />
      <Wholesale />
      <FAQ />
      <Contact />
      <Footer />
      <WhatsAppButton />
    </>
  );
};

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);

  useEffect(() => {
    const savedCart = localStorage.getItem('burbujasCart');
    if (savedCart) {
      const items = JSON.parse(savedCart);
      setCartItemsCount(items.length);
    }

    const handleCartUpdate = (e) => {
      setCartItemsCount(e.detail);
    };

    window.addEventListener('cartUpdate', handleCartUpdate);
    return () => window.removeEventListener('cartUpdate', handleCartUpdate);
  }, []);

  const handleAddToCart = (product) => {
    const savedCart = localStorage.getItem('burbujasCart');
    const cartItems = savedCart ? JSON.parse(savedCart) : [];
    
    const existingItemIndex = cartItems.findIndex(
      item => item.cartItemId === product.cartItemId
    );

    if (existingItemIndex >= 0) {
      cartItems[existingItemIndex].quantity += 1;
    } else {
      cartItems.push({ ...product, quantity: 1 });
    }

    localStorage.setItem('burbujasCart', JSON.stringify(cartItems));
    setCartItemsCount(cartItems.length);
    
    toast.success(`${product.name} agregado al carrito`, {
      description: `${product.selectedVariant.size} - $${product.selectedVariant.price} MXN`,
    });
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={
              <Home 
                onAddToCart={handleAddToCart}
                cartItemsCount={cartItemsCount}
                onCartOpen={() => setIsCartOpen(true)}
              />
            } 
          />
          <Route path="/checkout" element={<><Checkout /><WhatsAppButton /></>} />
          <Route path="/payment-success" element={<><PaymentResult status="success" /><WhatsAppButton /></>} />
          <Route path="/payment-failure" element={<><PaymentResult status="failure" /><WhatsAppButton /></>} />
          <Route path="/payment-pending" element={<><PaymentResult status="pending" /><WhatsAppButton /></>} />

          {/* Admin Panel */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route
            element={
              <AdminProtected>
                <AdminLayout />
              </AdminProtected>
            }
          >
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/products" element={<AdminProducts />} />
          </Route>
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
        </Routes>
        <Cart 
          isOpen={isCartOpen} 
          onClose={() => setIsCartOpen(false)} 
        />
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

export default App;
